import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";

/**
 * Debounced part-number autocomplete backed by /api/items.
 * Props:
 *  - value: current input string
 *  - onChange(v): called on every keystroke
 *  - onPick(item): called when user selects a suggestion (full item doc)
 *  - placeholder, className, testid
 */
export function PartNumberAutocomplete({ value, onChange, onPick, placeholder, className = "", testid, required, autoFocus }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0); // highlighted index
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!value || value.length < 1) { setItems([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/items", { params: { q: value } });
        setItems(data.slice(0, 8));
        setHi(0);
      } catch { setItems([]); }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const pick = (it) => {
    onPick?.(it);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && items[hi]) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        value={value}
        required={required}
        autoFocus={autoFocus}
        placeholder={placeholder || "Type part number…"}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onKeyDown={onKey}
        className="rounded-none"
        data-testid={testid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div
          className="absolute z-40 left-0 right-0 top-full mt-0 bg-white border border-slate-300 shadow-md max-h-64 overflow-auto"
          data-testid={`${testid}-suggestions`}
        >
          {items.map((it, i) => (
            <button
              type="button"
              key={it.id}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(it)}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 flex items-baseline gap-3 ${i === hi ? "bg-blue-50" : "hover:bg-slate-50"}`}
              data-testid={`suggestion-${it.id}`}
            >
              <span className="font-mono text-sm text-slate-900 font-medium">{it.part_number}</span>
              <span className="text-xs text-slate-500">{it.manufacturer || ""}</span>
              <span className="text-xs text-slate-600 truncate ml-auto max-w-[45%]">{it.description || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PartNumberAutocomplete;
